// The board (PvP depth, layer 1; design note mythic-idle-design/board.md): five creatures a side on two rows, all
// fighting at once, on the PvP ruleset (shared/pvp.ts) with targeting by row. Every PvP mode reaches it through
// shared/pvpfight.ts pvpBattle while the admin switch is on (kv 'board-on').
//
// - Line-up: one of each species, at most BOARD.teamMax creatures, up to BOARD.frontMax in the front row and the rest
//   at the back. A creature's slot (its place in the line-up) only breaks ties.
// - Reach from the species' attack style: Attack ('might') is melee and hits the enemy front row, the lowest HP there
//   first, until the front row is empty, then the back row. Magic ('focus') is ranged and can hit anyone: the enemy its
//   best ready move scores highest against (the type edge), ties to the lowest HP, then the slot. BOARD_MOVE_REACH lets
//   a move override this (a dive that reaches the back row); none does in season one.
// - Moves: the basic attack, Light, Heavy and Coverage on their cooldowns by the auto-cast rule (shared/combat.ts
//   pickMove), the PvP chart, crits and the 0.85-1 roll. The Wave (Lv 60) hits a whole enemy row at WAVE_SPLASH, and
//   is cast first whenever it is ready and that row has at least WAVE_MIN_TARGETS creatures standing.
// - Signature and Awakened moves fire from a charge bar, not a cooldown: every cast fills BOARD.charge.perBlow, every
//   blow taken fills perHpTaken × the share of max HP lost (at most hitCap a blow), so the front row charges by taking
//   the hits. On the first beat with a full bar the creature casts its charge move and the bar empties; an Awakened
//   form casts its Awakened move, then its Signature on its next beat. The Awakened move keeps its own 30 s cooldown
//   (rules 1: none, so every full bar fired both; BoardRules.awakened).
// - Pacing: HP × BOARD.hpMult; from overtimeMs all damage doubles every overtimeDoubleMs; at capMs the side with more
//   creatures standing wins, then the larger share of HP left, then the defender (side 1).
// - Openings and initiative are fixed: each move enters with 65% of its cooldown run, and a creature's first beat comes
//   at 0.45 of a beat if it is faster than the enemy team's average Speed, else at 0.8. Every draw (crits, rolls) comes
//   from the fight's seed, so a fight replays exactly.
// - Blows due at the same moment land together: each side's casts that moment read the enemy as its own side's earlier
//   casts left it, so no side owns a tie, and charge gained that moment counts from the next.
//
// - Held items (shared/held.ts): stat items, the Harpy Talon and the Spirit Bead live in the Fighter. The rest act
//   here as in battle(), where every creature "steps in" at the start: a Ward Rune's shield, a Thunder Phial's first
//   blow of the fight, an Oni Mask under half HP, a Bronze Mirror's return, a Fox Lantern's drain and a Mead Horn's
//   heal on a knockout. None reads the rows.
// - Board scaling (rules 2): the board turns small edges into wins more reliably than fronts in order, so synergies and
//   held items count for less here (BoardRules.synergyScale and heldScale, applied as the sides are built in
//   shared/pvpfight.ts). Every rules version stays in BOARD_RULES so a recorded game replays on its own numbers.
//
// The input is fighters as pvpFighter makes them (fight level, PvP stats, trait, relic, Rune Tablet, held item), so
// anything applied to a Fighter upstream (synergies) carries into the board unchanged.
import { SPECIES } from './species.ts';
import { MOVE, WAVE_MIN_TARGETS, WAVE_SPLASH, type MoveDef } from './moves.ts';
import { basicMove, beatMs, kitMoves, moveCooldownMs, moveScore, pickMove, strike, waveMove, type Fighter } from './combat.ts';
import { PVP, oneOfEach, openingCooldowns, pvpFighter, swiftAt, type PvpCreature, type PvpMode } from './pvp.ts';
import { powerParts } from './power.ts';
import type { Blow } from './arena.ts';
import type { HeldScale } from './held.ts';
import type { SynergyScale } from './synergy.ts';
import type { Rng } from './rng.ts';

export type BoardRow = 'front' | 'back';
/** From the species' attack style: Attack is melee, Magic is ranged. */
export type Reach = 'melee' | 'ranged';
/** How a single-target move finds its target: the melee rule ('front'), the ranged rule ('any'), or a dive ('back'). */
export type MoveReach = 'front' | 'any' | 'back';
/**
 * The ranged rule (the bench compares them, board.md): 'edge' the best type edge anywhere, ties to the lowest HP;
 * 'frontEdge' the same, but a tie stays on the front row; 'lowest' the lowest HP anywhere; 'nearest' the melee rule.
 */
export type RangedRule = 'edge' | 'frontEdge' | 'lowest' | 'nearest';

export interface ChargeRules {
  /** A full bar. */
  max: number;
  /** Filled by each cast the creature makes (a Wave counts once; charge moves give none). */
  perBlow: number;
  /** Filled by taking a blow: this × the share of max HP the blow took... */
  perHpTaken: number;
  /** ...at most this much from one blow. */
  hitCap: number;
}

export interface BoardRules {
  /** Rules version, for tournaments that lock a ruleset. */
  version: number;
  teamMax: number;
  frontMax: number;
  /** HP multiplier on the board (the 1v1 Arena's is PVP.hpMult). */
  hpMult: number;
  charge: ChargeRules;
  ranged: RangedRule;
  /** Overtime: from overtimeMs all damage doubles every overtimeDoubleMs. */
  overtimeMs: number;
  overtimeDoubleMs: number;
  /** The backstop: most creatures standing, then HP share, then the defender. */
  capMs: number;
  /**
   * Board-only scales on synergy perks (shared/synergy.ts) and held-item effects (shared/held.ts), applied when the
   * sides are built (shared/pvpfight.ts). Both were tuned on fronts in order, and the board turns small edges into wins
   * more reliably (all ten creatures fight at once, focus fire snowballs). Empty: the numbers as listed.
   */
  synergyScale: SynergyScale;
  heldScale: HeldScale;
  /**
   * What a full bar buys an Awakened form: its Awakened move, then its Signature on its next beat. 'withSignature'
   * (version 1): on every full bar. 'cooldown': the Awakened move also keeps its own cooldown (AWAKENED_CD_MS, entering
   * with 65% of it run as every move does), so a bar that fills before it is ready fires the Signature alone.
   */
  awakened: 'withSignature' | 'cooldown';
  /**
   * The per-fight swing: each creature fights at its fight level × (1 ± up to this), drawn once per fight from the
   * fight's seed before the first blow (shared/pvpfight.ts boardTeam). 0: none. Rows set by default are placed on the
   * unswung fighters.
   */
  swing: number;
  /**
   * An element trait counts for the creature's own element (shared/traits.ts ownTrait), applied as the sides are built
   * (shared/pvpfight.ts sideFighters). False (versions 1-3): the trait as stored, so a Radiant Leafcub gets nothing.
   */
  ownTraits: boolean;
  /** Relic damage counts at this power of its multiplier: 1 (in full) in rules 1 and 2, PVP.relicExp from rules 3. */
  relicExp: number;
}

/**
 * Every board rules version, kept so a recorded fight (tournament rules 7 and up, shared/tournament.ts) replays on its
 * own numbers. 1: season one as wired (2026-10-15). 2: 1 with the board scales (board.md "Board scaling"). 3: 2 with
 * the ±10% per-fight swing (2026-10-20, mythic-idle-reports/pvp-fairness-2026-10-20.md: a 5% Power edge wins about 80%
 * instead of 96%, a 20% edge still 99.8%). 4: 3 with element traits counting for the creature's own element (ownTraits)
 * and relics at their cube root (shared/pvp.ts PVP.relicExp), both 2026-10-20.
 */
export const BOARD_RULES: Readonly<Record<number, BoardRules>> = (() => {
  const v1: BoardRules = {
    version: 1,
    teamMax: 5,
    frontMax: 3,
    hpMult: 9,
    charge: { max: 100, perBlow: 4, perHpTaken: 200, hitCap: 25 },
    ranged: 'edge',
    overtimeMs: 45_000,
    overtimeDoubleMs: 5_000,
    capMs: 60_000,
    synergyScale: {},
    heldScale: {},
    awakened: 'withSignature',
    swing: 0,
    ownTraits: false,
    relicExp: 1,
  };
  // Rules 2 (npm run consistency and battle-bench on the board): every creature fights at once, so an edge reaches the
  // whole fight and the fronts-tuned numbers read high here. Rules 1 measured best synergy line-up 86% vs none (fronts
  // 74%), one item ×5 up to 83%, an Awakened slot 90% vs its base form at equal Power. Rules 2 scales Speed and Kin
  // most, region HP and damage least; the HP, Defense, Resist, reflect, element and drain items to 0.6, the
  // knockout-heal and crit items to 0.8; and the Awakened move keeps its 30 s cooldown as on fronts. Measured: best
  // synergy 78%, Yomi 4 65%, featured Midgard 4 67%, one item ×5 64-74%, five good items 69%, awakenedTeam5v5 0.89,
  // awakenedOverTier 0.23.
  const v2: BoardRules = {
    ...v1,
    version: 2,
    synergyScale: { hp: 0.9, dmg: 0.9, swift: 0.5, moves: 0.7, kin: 0.2 },
    heldScale: { hp: 0.6, guard: 0.6, will: 0.6, reflect: 0.6, element: 0.6, drain: 0.6, healKo: 0.8, crit: 0.8 },
    awakened: 'cooldown',
  };
  const v3: BoardRules = { ...v2, version: 3, swing: 0.1 };
  // Rules 4: element traits for the creature's own element, and relic damage at its cube root, as in every PvP mode
  // (a full set of Q100 Totems beat the same line-up 99%).
  const v4: BoardRules = { ...v3, version: 4, ownTraits: true, relicExp: PVP.relicExp };
  return { 1: v1, 2: v2, 3: v3, 4: v4 };
})();
/** The live board rules: the latest version. */
export const BOARD: BoardRules = BOARD_RULES[4];
/** A board rules version's rules (absent or unknown: the live ones). */
export const boardRules = (v?: number): BoardRules => (v !== undefined && BOARD_RULES[v]) || BOARD;

/**
 * Moves that reach differently from their owner's style, by move id: 'back' is a dive (the back row first, then the
 * front), 'any' lets a melee creature's move pick like a ranged one, 'front' keeps a ranged creature's move on the
 * front row. Empty in season one; a later season can give a few Signatures a dive here.
 */
export const BOARD_MOVE_REACH: Readonly<Record<string, MoveReach>> = {};

export type BoardOpts = Partial<Omit<BoardRules, 'charge'>> & {
  charge?: Partial<ChargeRules>;
  /** Overrides BOARD_MOVE_REACH (tests and the bench). */
  moveReach?: Readonly<Record<string, MoveReach>>;
};

/** One creature placed on the board. id, level and aura are carried to the result for the replay; they default to the slot, the fight level and false. */
export interface BoardEntry { f: Fighter; row: BoardRow; id?: number; level?: number; aura?: boolean }
export interface BoardTeam { name: string; team: BoardEntry[] }

/** A creature as the result shows it: BattleSide's fields plus its row and reach. */
export interface BoardSlot { id: number; species: string; level: number; arenaLevel: number; maxHp: number; aura: boolean; row: BoardRow; reach: Reach }
export interface BoardSide { name: string; team: BoardSlot[] }
/** A blow (shared/arena.ts Blow) plus both creatures' charge after that moment (0..BOARD.charge.max), for the replay's bars. */
export interface BoardBlow extends Blow { ca: number; cd: number }
/** Shaped like shared/arena.ts BattleResult, so the same replay plumbing can carry it. board: the rules version (BOARD.version). */
export interface BoardResult { sides: [BoardSide, BoardSide]; log: BoardBlow[]; winner: 0 | 1; ms: number; timedOut: boolean; board: number }

/** A creature's reach: Attack (might) is melee, Magic (focus) is ranged. */
export const reachOf = (speciesId: string): Reach => (SPECIES[speciesId].style === 'might' ? 'melee' : 'ranged');

/** A PvP creature as a board entry (pvpFighter: fight level, PvP rolls, relic, Rune Tablet). */
export function boardEntry(c: PvpCreature & { id?: number }, row: BoardRow, mode: PvpMode = 'arena'): BoardEntry {
  return { f: pvpFighter(c, mode), row, id: c.id, level: c.level, aura: c.rolls.aura };
}

/**
 * The line-up as it fights: one of each species (a later copy sits out), at most teamMax, and at most frontMax in the
 * front row (a later front creature moves to the back).
 */
export function placeTeam(team: readonly BoardEntry[], rules: Pick<BoardRules, 'teamMax' | 'frontMax'> = BOARD): BoardEntry[] {
  const kept = oneOfEach(team.map((e) => ({ e, species: e.f.speciesId }))).slice(0, rules.teamMax);
  let fronts = 0;
  return kept.map(({ e }) => ({ ...e, row: e.row === 'front' && fronts++ < rules.frontMax ? 'front' : 'back' }));
}

/**
 * A default placement ("tanks front, carries back"), for line-ups saved without rows and the bench's sensible
 * placement. Tank score: bulk × √(bulk ÷ damage) (shared/power.ts powerParts), so a creature that takes hits well and
 * deals less is the one to send in front. The highest score goes in front, with every other creature within
 * DEFAULT_FRONT_SHARE of it, at most frontMax. On the bench (npm run board) it ranks 3.5th of the 26 placements of
 * five on average and wins 84% against a random placement.
 */
export const DEFAULT_FRONT_SHARE = 0.8;
export function defaultRows(fighters: readonly Fighter[], rules: Pick<BoardRules, 'frontMax'> = BOARD): BoardRow[] {
  const tank = fighters.map((f) => { const p = powerParts(f); return p.bulk * Math.sqrt(p.bulk / Math.max(1e-9, p.dps)); });
  const top = Math.max(0, ...tank);
  const order = fighters.map((_, i) => i).sort((i, j) => tank[j] - tank[i] || i - j);
  const front = new Set(order.filter((i, k) => k < rules.frontMax && tank[i] >= DEFAULT_FRONT_SHARE * top));
  return fighters.map((_, i) => (front.has(i) ? 'front' : 'back'));
}

/** Overtime damage multiplier at `ms` into a board fight. */
export function boardOvertimeMult(ms: number, rules: Pick<BoardRules, 'overtimeMs' | 'overtimeDoubleMs'> = BOARD): number {
  return ms > rules.overtimeMs ? Math.pow(2, (ms - rules.overtimeMs) / rules.overtimeDoubleMs) : 1;
}

/** The winner at the backstop: more creatures standing, then the larger share of HP left, then the defender (side 1). */
export function capWinner(sides: readonly (readonly { hp: number; max: number }[])[]): 0 | 1 {
  const alive = sides.map((t) => t.filter((u) => u.hp > 0).length);
  if (alive[0] !== alive[1]) return alive[0] > alive[1] ? 0 : 1;
  const share = sides.map((t) => t.reduce((n, u) => n + u.hp, 0) / Math.max(1, t.reduce((n, u) => n + u.max, 0)));
  return share[0] > share[1] ? 0 : 1;
}

/** Charge a blow taken gives: perHpTaken × the share of max HP lost, at most hitCap. */
export function hitCharge(lost: number, maxHp: number, c: Pick<ChargeRules, 'perHpTaken' | 'hitCap'> = BOARD.charge): number {
  return Math.min(c.hitCap, (c.perHpTaken * lost) / Math.max(1, maxHp));
}

const isCharged = (m: MoveDef) => m.slot === 'signature' || m.slot === 'awakened';

/** Charge moves (Signature, Awakened) cast in a result, per side and slot. */
export function boardSignatures(r: Pick<BoardResult, 'sides' | 'log'>): number[][] {
  const out = r.sides.map((s) => s.team.map(() => 0));
  for (const b of r.log) if (b.mv && MOVE[b.mv] && isCharged(MOVE[b.mv])) out[b.s][b.a]++;
  return out;
}

interface Unit {
  s: 0 | 1; i: number; f: Fighter; row: BoardRow; reach: Reach; max: number; hp: number; next: number; beat: number;
  ready: Map<string, number>;
  /** Light, Heavy, Coverage: cast by the auto-cast rule. */
  cast: MoveDef[];
  /** Awakened then Signature: cast from the charge bar. */
  charged: MoveDef[];
  wave: MoveDef | null;
  charge: number;
  /** Charge gained this moment, added once every cast of the moment is done. */
  gain: number;
  /** Charge moves still to cast, one a beat. */
  queue: MoveDef[];
  /** A Ward Rune's shield left, and whether it has cast yet (a Thunder Phial's first cast). */
  shield: number;
  struck: boolean;
}

const EPS = 1e-9;

/**
 * A board fight. Side 1 is the defender. Pure: the same line-ups, seed and options give the same result, blow for blow.
 */
export function boardBattle(a: BoardTeam, b: BoardTeam, rng: Rng, opts: BoardOpts = {}): BoardResult {
  const R: BoardRules = { ...BOARD, ...opts, charge: { ...BOARD.charge, ...opts.charge } };
  const reachMap = opts.moveReach ?? BOARD_MOVE_REACH;
  const placed = [placeTeam(a.team, R), placeTeam(b.team, R)];
  const units: Unit[][] = placed.map((t, s) => t.map((e, i) => {
    const f = e.f;
    const max = Math.max(1, Math.round(f.stats.hp * R.hpMult));
    const kit = kitMoves(f);
    const wave = waveMove(f);
    const ready = openingCooldowns(f, 0);
    if (wave) ready.set(wave.id, moveCooldownMs(f, wave) * (1 - PVP.openRun));
    return {
      s: s as 0 | 1, i, f, row: e.row, reach: reachOf(f.speciesId), max, hp: max, next: Infinity, beat: beatMs(f), ready,
      cast: kit.filter((m) => !isCharged(m)),
      charged: [...kit.filter((m) => m.slot === 'awakened'), ...kit.filter((m) => m.slot === 'signature')],
      wave, charge: 0, gain: 0, queue: [],
      shield: f.held?.shield ? Math.round(max * f.held.shield) : 0, struck: false,
    };
  }));
  // Initiative: faster than the enemy team's average Speed acts at 0.45 of a beat, else at 0.8.
  for (const s of [0, 1] as const) {
    const foes = units[1 - s];
    const avg = foes.length ? foes.reduce((n, u) => n + swiftAt(u.f), 0) / foes.length : 0;
    for (const u of units[s]) u.next = u.beat * (swiftAt(u.f) > avg ? PVP.initFaster : PVP.initSlower);
  }

  const all = units.flat();
  const standing = units.map((t) => t.length);
  const log: BoardBlow[] = [];
  const dealt = [0, 0];
  let ms = 0;
  const isReady = (u: Unit, m: MoveDef) => (u.ready.get(m.id) ?? 0) <= ms;
  const lowest = (pool: Unit[]) => pool.reduce((best, x) => (x.hp < best.hp ? x : best));

  /** The target of a single-target move. `moves`: what the attacker could cast now (the ranged rule scores them). */
  const targetOf = (u: Unit, alive: Unit[], reach: MoveReach, moves: MoveDef[]): Unit => {
    if (reach !== 'any' || R.ranged === 'nearest') {
      const want = alive.filter((x) => x.row === (reach === 'back' ? 'back' : 'front'));
      return lowest(want.length ? want : alive);
    }
    if (R.ranged === 'lowest') return lowest(alive);
    let best = alive[0], bs = -Infinity;
    for (const x of alive) {
      let sc = 0;
      for (const m of moves) sc = Math.max(sc, moveScore(u.f, x.f, m, 'pvp'));
      if (sc > bs + EPS) { best = x; bs = sc; continue; }
      if (sc < bs - EPS) continue;
      // A tie: 'frontEdge' keeps to the front row, then the lowest HP; slot order keeps the earlier one.
      if (R.ranged === 'frontEdge' && x.row !== best.row) { if (x.row === 'front') best = x; continue; }
      if (x.hp < best.hp) best = x;
    }
    return best;
  };
  const moveReach = (u: Unit, m: MoveDef | null): MoveReach => (m && reachMap[m.id]) || (u.reach === 'melee' ? 'front' : 'any');

  /** One blow. An Oni Mask hits harder under half HP, a Thunder Phial's first blow of the fight harder (held items). */
  const hit = (u: Unit, t: Unit, m: MoveDef, share: number) => {
    const h = strike(u.f, t.f, m, 'pvp', rng, share);
    const rage = u.f.held?.rage && u.hp * 2 < u.max ? 1 + u.f.held.rage : 1;
    const first = u.f.held?.first && !u.struck ? 1 + u.f.held.first : 1;
    u.struck = true;
    const dmg = Math.max(1, Math.round(h.dmg * boardOvertimeMult(ms, R) * rage * first));
    const blow: BoardBlow = { ms: Math.round(ms), s: u.s, a: u.i, d: t.i, dmg, crit: h.crit, mult: h.mult, hp: t.hp, mv: h.mv, ca: 0, cd: 0 };
    let taken = dmg;
    if (t.shield > 0) { const sa = Math.min(t.shield, taken); t.shield -= sa; taken -= sa; blow.sa = sa; }
    const before = t.hp;
    const lost = Math.min(t.hp, taken);
    t.hp -= lost;
    blow.hp = t.hp;
    if (lost && !t.hp) standing[t.s]--;
    dealt[u.s] += dmg;
    t.gain += hitCharge(lost, t.max, R.charge);
    if (u.f.held || t.f.held) {
      // A Bronze Mirror returns part of what it lost, a Fox Lantern drains, a Mead Horn heals on a knockout.
      const ah = u.hp;
      if (t.f.held?.reflect && lost > 0 && u.hp > 0) u.hp = Math.max(0, u.hp - Math.round(lost * t.f.held.reflect));
      if (u.f.held?.drain && lost > 0 && u.hp > 0) u.hp = Math.min(u.max, u.hp + Math.round(lost * u.f.held.drain));
      if (u.f.held?.healKo && !t.hp && before > 0 && u.hp > 0) u.hp = Math.min(u.max, u.hp + Math.round(u.max * u.f.held.healKo));
      if (u.hp !== ah) { blow.ah = u.hp; if (!u.hp && ah > 0) standing[u.s]--; }
    }
    log.push(blow);
  };

  const act = (u: Unit) => {
    const alive = units[1 - u.s].filter((x) => x.hp > 0);
    if (!alive.length) return;
    // 1. A charge move: one already queued, or a full bar.
    let cm = u.queue.shift();
    if (!cm && u.charged.length && u.charge >= R.charge.max) {
      u.charge = 0;
      [cm, ...u.queue] = R.awakened === 'cooldown' ? u.charged.filter((m) => m.slot !== 'awakened' || isReady(u, m)) : u.charged;
    }
    if (cm) {
      if (cm.slot === 'awakened' && R.awakened === 'cooldown') u.ready.set(cm.id, ms + moveCooldownMs(u.f, cm));
      hit(u, targetOf(u, alive, moveReach(u, cm), [cm]), cm, 1);
      return;
    }
    // 2. The Wave, on a row with at least WAVE_MIN_TARGETS standing: melee the front row while it stands, ranged the fuller row.
    if (u.wave && isReady(u, u.wave)) {
      const front = alive.filter((x) => x.row === 'front'), back = alive.filter((x) => x.row === 'back');
      const reach = moveReach(u, u.wave);
      const row = reach === 'front' ? (front.length ? front : back)
        : reach === 'back' ? (back.length ? back : front)
        : back.length > front.length ? back : front.length ? front : back;
      if (row.length >= WAVE_MIN_TARGETS) {
        u.ready.set(u.wave.id, ms + moveCooldownMs(u.f, u.wave));
        for (const t of row) hit(u, t, u.wave, WAVE_SPLASH);
        u.gain += R.charge.perBlow;
        return;
      }
    }
    // 3. The auto-cast rule on the target.
    const readyNow = (m: MoveDef) => !isCharged(m) && isReady(u, m);
    const t = targetOf(u, alive, moveReach(u, null), [basicMove(u.f), ...u.cast.filter(readyNow)]);
    const m = pickMove(u.f, t.f, 'pvp', readyNow, t.hp);
    if (m.slot !== 'basic') u.ready.set(m.id, ms + moveCooldownMs(u.f, m));
    hit(u, t, m, 1);
    u.gain += R.charge.perBlow;
  };

  const strikers: Unit[] = [];
  while (standing[0] && standing[1]) {
    let now = Infinity;
    for (const u of all) if (u.hp > 0 && u.next < now) now = u.next;
    if (now > R.capMs) { ms = R.capMs; break; }
    ms = now;
    // Everyone due now acts, side 0 then side 1, each in slot order; a creature felled this moment still acts.
    strikers.length = 0;
    for (const u of all) if (u.hp > 0 && u.next === now) strikers.push(u);
    const from = log.length;
    for (const u of strikers) act(u);
    for (const u of all) if (u.gain) { u.charge = Math.min(R.charge.max, u.charge + u.gain); u.gain = 0; }
    for (let k = from; k < log.length; k++) {
      const x = log[k];
      x.ca = Math.round(units[x.s][x.a].charge);
      x.cd = Math.round(units[1 - x.s][x.d].charge);
    }
    for (const u of strikers) u.next = now + u.beat;
  }

  const timedOut = standing[0] > 0 && standing[1] > 0;
  let winner: 0 | 1;
  // Both sides fell at the same moment: more damage dealt wins, then the defender.
  if (!standing[0] && !standing[1]) winner = dealt[0] > dealt[1] ? 0 : 1;
  else if (!timedOut) winner = standing[0] ? 0 : 1;
  else winner = capWinner(units);

  const side = (name: string, s: number): BoardSide => ({
    name,
    team: units[s].map((u) => {
      const e = placed[s][u.i];
      return { id: e.id ?? u.i + 1, species: u.f.speciesId, level: e.level ?? u.f.level, arenaLevel: u.f.level, maxHp: u.max, aura: !!e.aura, row: u.row, reach: u.reach };
    }),
  });
  return { sides: [side(a.name, 0), side(b.name, 1)], log, winner, ms: Math.round(ms), timedOut, board: R.version };
}
